// API credentials, routed at `/api-keys`.
//
// Two kinds of credential live in one screen because they are one table
// server-side and one decision for the reader: a **general key** (`sluice_ak_`)
// that acts with the owner's full authority, and a **fine-grained token**
// (`sluice_ft_`) carrying `{OrgID, InstanceID, Features}` — the owner's
// authority intersected with a scope that can only ever subtract (`01` §6.2).
//
// Keys are an ACCOUNT-level resource, not an organization one, which is why
// this route carries no `:orgId`: a key travels across every organization its
// owner belongs to, narrowed by its own scope.

import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "../components/index.js";
import {
  toast,
  type BnColumn,
  type BnKvRow,
  type BnRow,
  type BnSelectChangeDetail,
  type BnSelectOption,
} from "../components/index.js";

import {
  createApiKey,
  FEATURES,
  listApiKeys,
  revokeApiKey,
  type ApiKey,
  type ApiKeyCreated,
  type Feature,
  type KeyKind,
} from "../api/apiKeys.js";
import {
  confirmStepUpOtp,
  listPasskeys,
  requestStepUpOtp,
  verifyWithPasskey,
} from "../api/auth.js";
import { ApiError, parseError } from "../api/errors.js";
import { listInstances, type Instance } from "../api/instances.js";
import { listOrgs, type Organization } from "../api/orgs.js";
import { passkeyFailureMessage, webauthnSupported } from "../api/webauthn.js";

/** The expiry choices of `01` §5.3, in display order. */
type Lifetime = "7d" | "30d" | "90d" | "1y" | "custom" | "unlimited";

const DAY_SECONDS = 24 * 60 * 60;

/** The ten-year ceiling `01` §5.1 puts on `expiresIn`. */
const MAX_EXPIRES_IN = 10 * 365 * DAY_SECONDS;

const PRESET_SECONDS: Readonly<Record<string, number>> = {
  "7d": 7 * DAY_SECONDS,
  "30d": 30 * DAY_SECONDS,
  "90d": 90 * DAY_SECONDS,
  "1y": 365 * DAY_SECONDS,
};

const LIFETIMES: BnSelectOption[] = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "1y", label: "1 year" },
  { value: "custom", label: "Custom date", description: "Expires at the end of a day you pick." },
  {
    value: "unlimited",
    label: "Never expires",
    description: "Works until it is revoked. Confirmed separately before you verify.",
  },
];

/**
 * ORG_LEVEL_FEATURES are the features whose surfaces resolve their target at
 * **organization** granularity. An instance-scoped token can never reach them
 * whatever its feature set — `TokenScope.AllowsOrg` returns false the moment
 * `InstanceID` is set (`01` §6.2, `SC-S211`) — so the permission grid disables
 * them once an instance is chosen instead of offering a grant that would
 * silently do nothing. Honest labels (`concept.md` §7 principle 8) applied to a
 * checkbox: a field that never matches is disabled and explained, not offered.
 */
const ORG_LEVEL_FEATURES: readonly Feature[] = ["members", "invites", "billing"];

/**
 * page-api-keys is the API credential console (`/api-keys`).
 *
 * Features implemented here:
 *
 * - **`SC-F203` / `SC-S200`** — general keys (`sluice_ak_`). A general key acts
 *   with the owner's live role in every organization they belong to.
 * - **`SC-F204`, `SC-F205`, `SC-F206` / `SC-S201`, `SC-S211`** — fine-grained
 *   tokens (`sluice_ft_`) and the scope builder: organization → optional
 *   instance → feature grid. The builder's job is to make **a scope only ever
 *   subtracts** legible, so the resulting authority is stated in a sentence
 *   rather than shown as a JSON blob, and the narrowing stops at the instance
 *   because Sluice's own object model has no enforced namespace below it.
 * - **`SC-F205` / `SC-S206`** — the deliberate absence. There is no feature for
 *   security management, so no token can create or revoke keys, manage
 *   passkeys, mint a verification grant or sign the account out everywhere. The
 *   page says so as a designed property, because that absence is what closes
 *   the leaked-key → mint-more-keys loop.
 * - **`SC-F210` / `SC-S202`, `SC-S207`** — lifecycle: the expiry picker, the
 *   danger confirmation on "never expires" (which runs **before** verification
 *   so a five-minute grant is not spent on a form the user then abandons), and
 *   revocation, which is effective on the next request because the scope is
 *   rebuilt from the key row every time.
 * - **`SC-F211` / `SC-F011` / `SC-S011`, `SC-S012`** — the step-up gate.
 *   Creation is refused without a fresh proof of presence; the grant is
 *   single-use, five minutes old at most, and burns even on a mismatch, so a
 *   `verification_required` or `verification_invalid` answer re-opens the verify
 *   dialog **without discarding the form**.
 * - **`SC-F016`** — the created / revoked events land on the account's security
 *   history, which is `page-security`.
 *
 * The plaintext exists exactly once, in the create response. It is shown once,
 * with a copy action and an unmistakable statement that it will not be shown
 * again, and no later API response returns it.
 */
@customElement("page-api-keys")
export class PageApiKeys extends LitElement {
  // ── Listing ─────────────────────────────────────────────────────────────
  @state() private keys: ApiKey[] = [];
  @state() private loading = true;
  @state() private loadError: ApiError | null = null;

  // ── Create (both kinds share one modal) ─────────────────────────────────
  /** Which kind of credential the open create dialog mints; null = closed. */
  @state() private createKind: KeyKind | null = null;
  @state() private name = "";
  @state() private lifetime: Lifetime = "90d";
  @state() private customDate = "";
  @state() private scopeOrg = "";
  @state() private scopeInstance = "";
  @state() private features: Feature[] = [];
  @state() private creating = false;
  /** The one-time create response. Non-null exactly while the secret is on screen. */
  @state() private created: ApiKeyCreated | null = null;

  /** Which submit is paused behind the "never expires" confirmation. */
  @state() private unlimitedConfirm: KeyKind | null = null;

  // ── Step-up ─────────────────────────────────────────────────────────────
  /** Which submit is paused behind the verification dialog; null = closed. */
  @state() private verifyFor: KeyKind | null = null;
  @state() private otpId = "";
  @state() private otpCode = "";
  @state() private verifying = false;
  /** Tri-state: true = offer the passkey branch, false = hide it, null = not
   * known yet, so the branch is offered and the lookup retried on click. */
  @state() private hasPasskeys: boolean | null = null;

  // ── Scope data (organizations and instances) ────────────────────────────
  @state() private orgs: Organization[] = [];
  @state() private instances: Instance[] = [];
  @state() private scopeLoading = false;
  @state() private scopeError: ApiError | null = null;
  private scopeLoaded = false;

  // ── Revoke ──────────────────────────────────────────────────────────────
  @state() private revokeTarget: ApiKey | null = null;
  @state() private revoking = false;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 32ch;
      min-width: 0;
    }
    .head-actions {
      display: flex;
      gap: var(--bino-space-sm);
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .subtitle {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .stack {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .copy {
      margin: 0 0 var(--bino-space-md);
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .copy:last-child {
      margin-bottom: 0;
    }
    .copy strong {
      color: var(--bino-text);
      font-weight: 600;
    }
    .name {
      font-family: var(--bino-font-mono);
      color: var(--bino-text);
    }
    .note {
      margin: 0;
      padding: var(--bino-space-md) var(--bino-space-lg);
      border-top: 1px solid var(--bino-border);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }

    /* The failure state, shared by the page and the scope loader. */
    .fail {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
      padding: var(--bino-space-lg);
    }
    .fail-message {
      margin: 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
    }
    .fail-actions {
      display: flex;
      gap: var(--bino-space-sm);
    }

    /* The permission grid. "Select all" sits above a hairline so it reads as a
       control over the set rather than a tenth feature. */
    .feature-head {
      margin-bottom: var(--bino-space-sm);
      padding-bottom: var(--bino-space-sm);
      border-bottom: 1px solid var(--bino-border);
    }
    .feature-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
      gap: var(--bino-space-sm) var(--bino-space-md);
    }

    /* The resulting-authority panel: the scope builder's whole point. */
    .authority {
      margin-bottom: var(--bino-space-md);
    }
    .authority p {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
      line-height: 1.5;
    }
    .authority p:last-child {
      margin-bottom: 0;
    }
    .authority strong {
      color: var(--bino-text);
      font-weight: 600;
    }
    .authority .lede {
      color: var(--bino-text);
    }

    .reveal-actions {
      display: flex;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
    .never-list {
      margin: 0;
      padding-left: var(--bino-space-lg);
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      line-height: 1.6;
    }
    .never-list li {
      margin-bottom: var(--bino-space-xs);
    }
    .verify-branch {
      margin-bottom: var(--bino-space-md);
    }
    .verify-or {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      text-align: center;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  // ── Loading ─────────────────────────────────────────────────────────────

  private async load() {
    this.loading = true;
    this.loadError = null;
    try {
      // One page is the whole list: `01` §5.1 caps a user at 25 keys **per
      // kind**, below the 50-row default page size, so there is no pager here.
      const page = await listApiKeys();
      this.keys = page.items;
      // Scope ids only become names once the organizations and instances are
      // loaded, so fetch them when there is actually a scoped token to label.
      if (this.keys.some((k) => k.kind === "fine_grained")) void this.ensureScopeData();
    } catch (err) {
      this.loadError = parseError(err);
    } finally {
      this.loading = false;
    }
  }

  /**
   * ensureScopeData loads the organizations and instances the scope builder
   * offers and the Scope column labels with. It runs once; a failure clears the
   * guard so the next trigger — reopening the dialog, pressing Retry — tries
   * again rather than leaving the page permanently unable to name a scope.
   */
  private async ensureScopeData() {
    if (this.scopeLoaded || this.scopeLoading) return;
    this.scopeLoading = true;
    this.scopeError = null;
    try {
      const [orgs, instances] = await Promise.all([listOrgs(), this.loadAllInstances()]);
      this.orgs = orgs.items;
      this.instances = instances;
      this.scopeLoaded = true;
    } catch (err) {
      this.scopeError = parseError(err);
    } finally {
      this.scopeLoading = false;
    }
  }

  /**
   * loadAllInstances pages through the instance list. The picker must be able
   * to offer every instance the caller can see, and `06` §1.5 caps a page at
   * 200; the five-page guard bounds a pathological account rather than the
   * product, which quota-limits instances long before this.
   */
  private async loadAllInstances(): Promise<Instance[]> {
    const out: Instance[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < 5; page++) {
      const res = await listInstances({ limit: 200, cursor });
      out.push(...res.items);
      if (res.next === null) break;
      cursor = res.next;
    }
    return out;
  }

  // ── Create ──────────────────────────────────────────────────────────────

  private openCreate(kind: KeyKind) {
    this.name = "";
    this.lifetime = "90d";
    this.customDate = "";
    this.scopeOrg = "";
    this.scopeInstance = "";
    this.features = [];
    this.created = null;
    this.createKind = kind;
    if (kind === "fine_grained") void this.ensureScopeData();
  }

  /** closeCreate refuses while a submit, a confirmation or a verification is in
   * flight — closing the form underneath any of those would strand the flow. */
  private closeCreate() {
    if (this.creating || this.unlimitedConfirm !== null || this.verifyFor !== null) return;
    this.createKind = null;
    this.created = null;
  }

  /**
   * expiresIn maps the picker to the create body's `expires_in_seconds`:
   * a positive number, or **null for unlimited** — which the API defines as "no
   * expiry" rather than "zero seconds". `undefined` means the picker does not
   * resolve to a lifetime at all (an empty, past or beyond-ten-years custom
   * date) and is what disables Create.
   */
  private expiresIn(): number | null | undefined {
    if (this.lifetime === "unlimited") return null;
    if (this.lifetime !== "custom") return PRESET_SECONDS[this.lifetime];
    if (this.customDate === "") return undefined;
    // A bare `YYYY-MM-DD` parses as UTC midnight; the explicit time makes it
    // **local end-of-day**, so "expires today" still means later today
    // (`SC-S202`).
    const end = new Date(`${this.customDate}T23:59:59`);
    if (Number.isNaN(end.getTime())) return undefined;
    const seconds = Math.floor((end.getTime() - Date.now()) / 1000);
    if (seconds <= 0 || seconds > MAX_EXPIRES_IN) return undefined;
    return seconds;
  }

  /** Create is enabled on a name, a resolvable lifetime and — for a scoped
   * token — at least one feature (`SC-S201`). */
  private get formValid(): boolean {
    if (this.name.trim() === "" || this.expiresIn() === undefined) return false;
    return this.createKind === "general" || this.features.length > 0;
  }

  /** The features actually grantable right now: an instance-scoped token can
   * never reach the organization-level ones, so they are neither offered nor
   * counted by "select all". */
  private get grantableFeatures(): Feature[] {
    return FEATURES.filter(
      (f) => this.scopeInstance === "" || !ORG_LEVEL_FEATURES.includes(f.id),
    ).map((f) => f.id);
  }

  private toggleFeature(id: Feature, on: boolean) {
    const rest = this.features.filter((f) => f !== id);
    this.features = on ? [...rest, id] : rest;
  }

  private onOrgChange(org: string) {
    this.scopeOrg = org;
    // Dropping the instance is not tidiness: `instance` requires `org`
    // server-side, and an instance from the previous organization would be
    // rejected as not belonging to this one (`01` §5.3 step 2).
    this.scopeInstance = "";
  }

  private onInstanceChange(instance: string) {
    this.scopeInstance = instance;
    // Narrowing to an instance makes the organization-level features
    // unreachable, so drop them rather than sending a grant that cannot apply.
    if (instance !== "") {
      this.features = this.features.filter((f) => !ORG_LEVEL_FEATURES.includes(f));
    }
  }

  /**
   * submit is the single create path for both kinds, re-entered twice: once
   * when the "never expires" confirmation is accepted, once when a verification
   * grant arrives. The order is load-bearing (`SC-S202`): a confirmation the
   * user might decline must come **before** the grant is minted, because a
   * grant is worth exactly one attempt and lives five minutes.
   */
  private async submit(opts: { confirmed?: boolean; verification?: string } = {}) {
    const kind = this.createKind;
    const expiresIn = this.expiresIn();
    if (kind === null || !this.formValid || expiresIn === undefined || this.creating) return;

    if (this.lifetime === "unlimited" && !opts.confirmed) {
      this.unlimitedConfirm = kind;
      return;
    }
    if (!opts.verification) {
      this.openVerify(kind);
      return;
    }

    this.creating = true;
    try {
      const result = await createApiKey(
        {
          name: this.name.trim(),
          kind,
          expires_in_seconds: expiresIn,
          ...(kind === "fine_grained"
            ? {
                scope: {
                  ...(this.scopeOrg ? { org: this.scopeOrg } : {}),
                  ...(this.scopeInstance ? { instance: this.scopeInstance } : {}),
                  features: [...this.features],
                },
              }
            : {}),
        },
        { verification: opts.verification },
      );
      this.created = result;
      // Prepend the row rather than reloading: the create response is the
      // authoritative view of the new key, and a reload would race the reveal.
      // The plaintext is stripped on the way into the listing — it belongs on
      // screen once and in the caller's secret store, not in an array that
      // outlives the dialog.
      const { secret, ...row } = result;
      void secret;
      this.keys = [row, ...this.keys];
      toast(kind === "general" ? "API key created" : "Fine-grained token created", "success");
    } catch (err) {
      this.createFailed(err, kind);
    } finally {
      this.creating = false;
    }
  }

  /**
   * createFailed routes a create failure. A verification code means the grant
   * was missing, already spent or older than five minutes — `Consume` burns the
   * entry even on a mismatch — so the verify dialog re-opens over the form,
   * which is left exactly as the user typed it (`SC-S011`, `SC-S202`).
   */
  private createFailed(err: unknown, kind: KeyKind) {
    const parsed = parseError(err);
    // `quota_exceeded` here is the per-account cap of 25 credentials **per
    // kind**, not an organization quota, so the server's own message — which
    // names the limit that was hit — is more accurate than the shared copy map.
    const text =
      parsed.code === "quota_exceeded" && parsed.serverMessage
        ? parsed.requestId
          ? `${parsed.serverMessage} (reference ${parsed.requestId})`
          : parsed.serverMessage
        : this.failureText(parsed);
    toast(text, "error");
    if (parsed.code === "verification_required" || parsed.code === "verification_invalid") {
      this.openVerify(kind);
    }
  }

  private confirmUnlimited() {
    const kind = this.unlimitedConfirm;
    this.unlimitedConfirm = null;
    if (kind !== null) void this.submit({ confirmed: true });
  }

  // ── Step-up verification (`SC-F011`, `SC-F211`) ─────────────────────────

  /** openVerify pauses a submit behind the verification dialog. The OTP
   * sub-state resets on every open: codes and grants are single-use, so
   * anything left from a previous round could only produce `otp_invalid`. */
  private openVerify(kind: KeyKind) {
    this.otpId = "";
    this.otpCode = "";
    this.verifyFor = kind;
    void this.ensurePasskeyInfo();
  }

  private closeVerify() {
    if (this.verifying) return;
    this.verifyFor = null;
  }

  /**
   * ensurePasskeyInfo decides whether the passkey branch is worth offering:
   * WebAuthn support plus at least one registered credential (`SC-S012`). A
   * failed lookup leaves the answer **null**, which keeps the branch visible in
   * an unknown state and retries on click — hiding it permanently because one
   * request failed would strip a working sign-in method off the dialog.
   */
  private async ensurePasskeyInfo() {
    if (this.hasPasskeys !== null) return;
    if (!webauthnSupported()) {
      this.hasPasskeys = false;
      return;
    }
    try {
      this.hasPasskeys = (await listPasskeys()).length > 0;
    } catch {
      // Deliberately left null; see the doc comment.
    }
  }

  /** resumeVerified closes the dialog and re-enters the paused submit with the
   * grant. `confirmed` is true because the unlimited gate, if it was due, ran
   * before verification. */
  private resumeVerified(grant: string) {
    this.verifyFor = null;
    void this.submit({ confirmed: true, verification: grant });
  }

  private async verifyByPasskey() {
    if (this.verifying) return;
    this.verifying = true;
    try {
      // An unknown answer is retried here rather than on render, which is what
      // makes the branch self-healing after a failed lookup.
      await this.ensurePasskeyInfo();
      const grant = await verifyWithPasskey({ action: "create_api_key" });
      this.resumeVerified(grant);
    } catch (err) {
      // A dismissed authenticator prompt is a cancellation: the dialog stays
      // open, nothing is said, and the email branch is still there.
      const message = passkeyFailureMessage(err);
      if (message) toast(message, "error");
    } finally {
      this.verifying = false;
    }
  }

  private async sendCode() {
    if (this.verifying) return;
    this.verifying = true;
    try {
      const { otpId } = await requestStepUpOtp();
      this.otpId = otpId;
      this.otpCode = "";
    } catch (err) {
      toast(this.failureText(parseError(err)), "error");
    } finally {
      this.verifying = false;
    }
  }

  private async confirmCode() {
    const code = this.otpCode.trim();
    if (code === "" || this.otpId === "" || this.verifying) return;
    this.verifying = true;
    try {
      const grant = await confirmStepUpOtp(this.otpId, code, { action: "create_api_key" });
      this.resumeVerified(grant);
    } catch (err) {
      // The server answers one uniform `otp_invalid` for wrong, expired and
      // foreign codes alike, so there is exactly one failure path to render.
      toast(this.failureText(parseError(err)), "error");
    } finally {
      this.verifying = false;
    }
  }

  // ── Revoke ──────────────────────────────────────────────────────────────

  private async confirmRevoke() {
    const target = this.revokeTarget;
    if (!target || this.revoking) return;
    this.revoking = true;
    try {
      await revokeApiKey(target.id);
      this.keys = this.keys.filter((k) => k.id !== target.id);
      this.revokeTarget = null;
      toast("Credential revoked", "success");
    } catch (err) {
      toast(this.failureText(parseError(err)), "error");
    } finally {
      this.revoking = false;
    }
  }

  // ── Formatting and lookups ──────────────────────────────────────────────

  private async copyToClipboard(text: string, label: string) {
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(text);
      toast(`${label} copied`, "success");
    } catch {
      // Say what happened rather than confirming something that did not.
      toast("Could not copy — select the value and copy it manually.", "error");
    }
  }

  private fmtDate(iso: string): string {
    if (!iso) return "—";
    const at = new Date(iso);
    if (Number.isNaN(at.getTime())) return iso;
    return at.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  }

  private isExpired(key: ApiKey): boolean {
    if (!key.expires) return false;
    const at = new Date(key.expires);
    return !Number.isNaN(at.getTime()) && at.getTime() <= Date.now();
  }

  private orgLabel(id: string): string {
    const org = this.orgs.find((o) => o.id === id);
    return org ? org.display_name || org.name : id;
  }

  private instanceLabel(id: string): string {
    const instance = this.instances.find((i) => i.id === id);
    return instance ? instance.display_name || instance.name : id;
  }

  private failureText(err: ApiError): string {
    return err.requestId ? `${err.message} (reference ${err.requestId})` : err.message;
  }

  // ── Render: shared pieces ───────────────────────────────────────────────

  /** renderFailure is this page's error state: the product's copy, then the
   * catalogue code and the request id as copyable rows, so a reader can quote a
   * reference in a support ticket (`OD-11`, `SC-S208`). */
  private renderFailure(err: ApiError, retry: () => void) {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Reference", value: err.requestId });
    return html`<div class="fail">
      <p class="fail-message" role="alert">${err.message}</p>
      <bn-kv
        .rows=${rows}
        @bn-copy=${(e: CustomEvent<{ label: string }>) => toast(`${e.detail.label} copied`)}
      ></bn-kv>
      ${err.requestId
        ? nothing
        : html`<p class="copy">
            This request never reached Sluice Cloud, so there is no reference to quote.
          </p>`}
      <div class="fail-actions">
        <bn-button variant="secondary" size="sm" @click=${retry}>Try again</bn-button>
      </div>
    </div>`;
  }

  private expiresCell(key: ApiKey) {
    if (!key.expires) return html`<bn-badge variant="neutral">Never</bn-badge>`;
    if (this.isExpired(key)) {
      // `SC-S207` keeps expired rows in the listing: a key that silently
      // vanished on expiry would look revoked, and the reader needs to see what
      // was provisioned.
      return html`<bn-badge variant="warning">Expired ${this.fmtDate(key.expires)}</bn-badge>`;
    }
    return this.fmtDate(key.expires);
  }

  private revokeCell(key: ApiKey) {
    return html`<bn-button
      variant="danger"
      size="sm"
      aria-label=${`Revoke ${key.name}`}
      @click=${() => (this.revokeTarget = key)}
      >Revoke</bn-button
    >`;
  }

  private lastUsedCell(key: ApiKey) {
    return key.last_used
      ? this.fmtDate(key.last_used)
      : html`<bn-badge variant="neutral">Never used</bn-badge>`;
  }

  // ── Render: listings ────────────────────────────────────────────────────

  private get generalKeys(): ApiKey[] {
    // An empty `kind` is read as general everywhere (`01` §5.1), so the general
    // list is "not fine-grained" rather than "kind === general".
    return this.keys.filter((k) => k.kind !== "fine_grained");
  }

  private get scopedKeys(): ApiKey[] {
    return this.keys.filter((k) => k.kind === "fine_grained");
  }

  private renderGeneralCard() {
    const columns: BnColumn[] = [
      { key: "name", label: "Name" },
      { key: "prefix", label: "Prefix", mono: true },
      { key: "created", label: "Created", width: "1px" },
      { key: "expires", label: "Expires", width: "1px" },
      { key: "lastUsed", label: "Last used", width: "1px" },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    const rows: BnRow[] = this.generalKeys.map((k) => ({
      name: k.name,
      prefix: k.display_prefix,
      created: this.fmtDate(k.created),
      expires: this.expiresCell(k),
      lastUsed: this.lastUsedCell(k),
      actions: this.revokeCell(k),
    }));
    return html`<bn-card flush heading="General keys">
      <bn-button
        slot="actions"
        variant="primary"
        size="sm"
        @click=${() => this.openCreate("general")}
        >New general key</bn-button
      >
      <bn-table
        caption="General API keys"
        .columns=${columns}
        .rows=${rows}
        empty="No general keys."
      ></bn-table>
      <p class="note">
        Prefix <code class="name">sluice_ak_</code>. A general key acts with your own authority:
        your live role in each organization you belong to, and nothing more. Lower your role and
        every key you hold narrows with it, on the next request.
      </p>
    </bn-card>`;
  }

  private renderScopedCard() {
    const columns: BnColumn[] = [
      { key: "name", label: "Name" },
      { key: "prefix", label: "Prefix", mono: true },
      { key: "scope", label: "Reaches" },
      { key: "permissions", label: "Permissions" },
      { key: "created", label: "Created", width: "1px" },
      { key: "expires", label: "Expires", width: "1px" },
      { key: "lastUsed", label: "Last used", width: "1px" },
      { key: "actions", label: "", align: "right", width: "1px" },
    ];
    const rows: BnRow[] = this.scopedKeys.map((k) => ({
      name: k.name,
      prefix: k.display_prefix,
      scope: this.scopeSummary(k),
      permissions: this.permissionsCell(k),
      created: this.fmtDate(k.created),
      expires: this.expiresCell(k),
      lastUsed: this.lastUsedCell(k),
      actions: this.revokeCell(k),
    }));
    return html`<bn-card flush heading="Fine-grained tokens">
      <bn-button
        slot="actions"
        variant="primary"
        size="sm"
        @click=${() => this.openCreate("fine_grained")}
        >New fine-grained token</bn-button
      >
      <bn-table
        caption="Fine-grained tokens"
        .columns=${columns}
        .rows=${rows}
        empty="No fine-grained tokens."
      ></bn-table>
      <p class="note">
        Prefix <code class="name">sluice_ft_</code>. A scope only ever subtracts: a token's
        authority is your role intersected with the organization, instance and permissions below —
        never more than you can do yourself.
      </p>
    </bn-card>`;
  }

  /** scopeSummary is the Reaches column: where a token may act, in words. */
  private scopeSummary(key: ApiKey): string {
    const scope = key.scope;
    if (!scope?.org) return "All your organizations";
    const org = this.orgLabel(scope.org);
    return scope.instance ? `${org} → ${this.instanceLabel(scope.instance)}` : org;
  }

  /** permissionsCell names the granted features. The full list is the value —
   * a bare count would hide exactly the thing a reader is auditing. */
  private permissionsCell(key: ApiKey) {
    const granted = key.scope?.features ?? [];
    if (granted.length === 0) return html`<bn-badge variant="neutral">None</bn-badge>`;
    const labels = granted.map((f) => FEATURES.find((x) => x.id === f)?.label ?? f);
    return labels.join(", ");
  }

  /**
   * renderNever states `SC-F205`'s deliberate absence. It belongs on the page
   * rather than in a document because it is the property that bounds the blast
   * radius of a leak, and a reader deciding how much to trust a CI credential
   * needs to know it without leaving the screen.
   */
  private renderNever() {
    return html`<bn-card heading="What no credential here can ever do">
      <p class="copy">
        There is <strong>no permission for security management</strong>, and that is a design
        decision rather than a gap. It is what stops a leaked credential from bootstrapping itself
        into a bigger one.
      </p>
      <ul class="never-list">
        <li>
          <strong>No fine-grained token</strong> can create, list or revoke API keys, manage
          passkeys, obtain a verification grant, sign you out everywhere, delete an organization,
          accept an invitation, run the playground or write secret material. No permission covers
          those routes, so the answer is <code class="name">403 insufficient_scope</code>.
        </li>
        <li>
          <strong>A general key</strong> may list and revoke keys — so rotation can be scripted —
          but it can never mint one by itself: creation needs a verification that only a signed-in
          browser session can obtain. It cannot touch your passkeys or your sessions either.
        </li>
        <li>
          <strong>Neither</strong> receives the contents of an audit record — the SQL sample, the
          subject's email and groups, the client metadata — however much of the audit permission it
          carries. The index and the aggregates stay readable; the record bodies do not travel.
        </li>
      </ul>
    </bn-card>`;
  }

  private renderListing() {
    if (this.loading) {
      return html`<bn-card flush>
        <bn-spinner block label="Loading credentials…"></bn-spinner>
      </bn-card>`;
    }
    if (this.loadError) {
      return html`<bn-card flush>${this.renderFailure(this.loadError, () => void this.load())}</bn-card>`;
    }
    if (this.keys.length === 0) {
      return html`<bn-card flush>
        <bn-empty-state
          icon="⌘"
          heading="No API credentials yet"
          message="Create a general key to script anything you can do in the UI, or a fine-grained token that can do exactly one job on exactly one instance."
        >
          <bn-button slot="actions" variant="primary" @click=${() => this.openCreate("general")}
            >New general key</bn-button
          >
          <bn-button
            slot="actions"
            variant="secondary"
            @click=${() => this.openCreate("fine_grained")}
            >New fine-grained token</bn-button
          >
        </bn-empty-state>
      </bn-card>`;
    }
    return html`${this.renderGeneralCard()} ${this.renderScopedCard()}`;
  }

  // ── Render: create dialog ───────────────────────────────────────────────

  private renderCreateModal() {
    const kind = this.createKind;
    const created = this.created;
    const done = created !== null;
    const noun = kind === "fine_grained" ? "fine-grained token" : "API key";
    return html`<bn-modal
      ?open=${kind !== null}
      width=${620}
      heading=${done ? `Your new ${noun}` : `Create a ${noun}`}
      @close=${() => this.closeCreate()}
    >
      ${created ? this.renderReveal(created) : this.renderCreateForm(kind)}
      ${done
        ? html`<p slot="consequence">
              This is the only time the secret is shown. Sluice Cloud stores a hash of it and cannot
              show it again — if you lose it, revoke this ${noun} and create another.
            </p>
            <bn-button slot="footer" variant="primary" @click=${() => this.closeCreate()}
              >Done</bn-button
            >`
        : html`<bn-button slot="footer" variant="ghost" @click=${() => this.closeCreate()}
              >Cancel</bn-button
            >
            <bn-button
              slot="footer"
              variant="primary"
              ?loading=${this.creating}
              ?disabled=${!this.formValid}
              @click=${() => void this.submit()}
              >Create ${noun}</bn-button
            >`}
    </bn-modal>`;
  }

  private renderCreateForm(kind: KeyKind | null) {
    return html`
      <bn-field
        label="Name"
        required
        hint="A label to recognise this credential later — “ci-policy-gate”, “laptop”."
      >
        <bn-input
          .value=${this.name}
          placeholder=${kind === "fine_grained" ? "ci-policy-gate" : "laptop"}
          autocomplete="off"
          ?disabled=${this.creating}
          @input=${(e: CustomEvent<{ value: string }>) => (this.name = e.detail.value)}
        ></bn-input>
      </bn-field>
      ${this.renderLifetimeField()}
      ${kind === "fine_grained" ? this.renderScopeBuilder() : this.renderGeneralNote()}
    `;
  }

  private renderGeneralNote() {
    return html`<bn-panel eyebrow="Resulting authority">
      <div class="authority">
        <p class="lede">
          This key acts <strong>as you</strong>: your live role in every organization you belong to,
          in every instance in them.
        </p>
        <p>
          It is never more than you can do yourself, and it narrows the instant your role does. If
          that is wider than the job needs, create a fine-grained token instead — the scope on one
          of those can only subtract.
        </p>
        <p>
          It can list and revoke API keys, so rotation can be automated. It can never create one:
          that needs a verification only a browser session can obtain. It can never manage your
          passkeys or sign you out everywhere.
        </p>
      </div>
    </bn-panel>`;
  }

  private renderLifetimeField() {
    const custom = this.lifetime === "custom";
    const invalidDate = custom && this.customDate !== "" && this.expiresIn() === undefined;
    return html`
      <bn-field
        label="Expiry"
        required
        hint=${this.lifetime === "unlimited"
          ? "Works until it is revoked. You will be asked to confirm that separately."
          : "How long the credential stays valid. Shorter is safer."}
      >
        <bn-select
          .options=${LIFETIMES}
          .value=${this.lifetime}
          ?disabled=${this.creating}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
            (this.lifetime = e.detail.value as Lifetime)}
        ></bn-select>
      </bn-field>
      ${custom
        ? html`<bn-field label="Expiry date" required>
            <bn-input
              type="date"
              .value=${this.customDate}
              ?disabled=${this.creating}
              diagnostic=${invalidDate
                ? "Pick a date in the future and within the next ten years. The credential expires at the end of that day, in your local time."
                : ""}
              @input=${(e: CustomEvent<{ value: string }>) => (this.customDate = e.detail.value)}
            ></bn-input>
          </bn-field>`
        : nothing}
    `;
  }

  // ── Render: the scope builder ───────────────────────────────────────────

  private renderScopeBuilder() {
    if (this.scopeLoading) {
      return html`<bn-spinner block label="Loading organizations and instances…"></bn-spinner>`;
    }
    if (this.scopeError) {
      return this.renderFailure(this.scopeError, () => {
        this.scopeLoaded = false;
        void this.ensureScopeData();
      });
    }

    const orgOptions: BnSelectOption[] = [
      {
        value: "",
        label: "All your organizations",
        description: "Every organization you belong to, now and in future.",
      },
      ...this.orgs.map((o) => ({
        value: o.id,
        label: o.display_name || o.name,
        description: o.personal ? "Your personal organization." : undefined,
      })),
    ];
    const instanceOptions: BnSelectOption[] = [
      {
        value: "",
        label: "The entire organization",
        description: "Every instance in it, including ones created later.",
      },
      ...this.instances
        .filter((i) => i.org === this.scopeOrg)
        .map((i) => ({
          value: i.id,
          label: i.display_name || i.name,
          description: i.hostname,
        })),
    ];

    return html`
      <bn-field
        label="Organization"
        hint="Where the token may act. Narrowing stops at the instance — Sluice has no enforced namespace below one, so a third level would be authority the gateway cannot express."
      >
        <bn-select
          .options=${orgOptions}
          .value=${this.scopeOrg}
          ?disabled=${this.creating}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => this.onOrgChange(e.detail.value)}
        ></bn-select>
      </bn-field>

      ${this.scopeOrg !== ""
        ? html`<bn-field label="Instance" hint="Narrow the token to a single instance.">
            <bn-select
              .options=${instanceOptions}
              .value=${this.scopeInstance}
              ?disabled=${this.creating}
              @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
                this.onInstanceChange(e.detail.value)}
            ></bn-select>
          </bn-field>`
        : nothing}

      ${this.renderFeatureGrid()} ${this.renderAuthority()}
    `;
  }

  private renderFeatureGrid() {
    const grantable = this.grantableFeatures;
    const allSelected = grantable.length > 0 && grantable.every((f) => this.features.includes(f));
    const instanceScoped = this.scopeInstance !== "";
    return html`<bn-field
      label="Permissions"
      required
      hint="The token may call only the surfaces you tick — and only where your own role already allows it."
    >
      <div class="feature-head">
        <bn-checkbox
          label="Select all"
          .checked=${allSelected}
          ?disabled=${this.creating}
          @change=${(e: CustomEvent<{ checked: boolean }>) =>
            (this.features = e.detail.checked ? [...grantable] : [])}
        ></bn-checkbox>
      </div>
      <div class="feature-grid">
        ${FEATURES.map((f) => {
          const unreachable = instanceScoped && ORG_LEVEL_FEATURES.includes(f.id);
          return html`<bn-checkbox
            label=${f.label}
            hint=${unreachable
              ? "Organization-level. An instance-scoped token can never reach it, so it cannot be granted here."
              : f.hint}
            .checked=${this.features.includes(f.id)}
            ?disabled=${this.creating || unreachable}
            @change=${(e: CustomEvent<{ checked: boolean }>) =>
              this.toggleFeature(f.id, e.detail.checked)}
          ></bn-checkbox>`;
        })}
      </div>
    </bn-field>`;
  }

  /**
   * renderAuthority is the centre of this page: the scope, restated as the
   * authority that results from it. A JSON blob would show what was configured;
   * this says what the credential will be able to do, which is the question the
   * reader actually has.
   */
  private renderAuthority() {
    const where =
      this.scopeOrg === ""
        ? "every organization you belong to, now and in future"
        : this.scopeInstance === ""
          ? `everything in ${this.orgLabel(this.scopeOrg)}, including instances added later`
          : `only ${this.instanceLabel(this.scopeInstance)}, in ${this.orgLabel(this.scopeOrg)}`;
    // Rendered in the vocabulary's canonical order rather than click order, so
    // the sentence reads the same for two people who ticked the same boxes.
    const ordered = FEATURES.filter((f) => this.features.includes(f.id)).map((f) => f.label);
    return html`<bn-panel eyebrow="Resulting authority">
      <div class="authority">
        <p class="lede">
          This token reaches <strong>${where}</strong>.
        </p>
        <p>
          It may use:
          <strong
            >${ordered.length > 0
              ? ordered.join(", ")
              : "nothing yet — tick at least one permission"}</strong
          >${ordered.length > 0 ? ". Every other surface answers 403." : "."}
        </p>
        <p>
          <strong>A scope only ever subtracts.</strong> The token acts as you, with your live role
          in each organization, narrowed by the choices above. It can never grant authority you do
          not have, and if your role is lowered — or you are removed — the token narrows on its very
          next request.
        </p>
        ${this.scopeInstance !== ""
          ? html`<p>
              Because it is narrowed to one instance, it can never reach organization-level
              surfaces — members, invitations, billing or organization settings — whatever
              permissions it carries.
            </p>`
          : nothing}
        <p>
          No permission covers security management, so this token can never create or revoke API
          keys, manage passkeys, obtain a verification, or sign you out everywhere.
        </p>
      </div>
    </bn-panel>`;
  }

  // ── Render: the one-time reveal ─────────────────────────────────────────

  private renderReveal(key: ApiKeyCreated) {
    const rows: BnKvRow[] = [
      { label: "Secret", value: key.secret },
      { label: "Name", value: key.name, copyable: false },
      {
        label: "Expires",
        value: key.expires ? this.fmtDate(key.expires) : "never",
        copyable: false,
      },
    ];
    return html`
      <p class="copy">
        Copy the secret into your secret store now. Paste it as
        <code class="name">Authorization: Bearer …</code> on any Sluice Cloud API call.
      </p>
      <bn-kv
        .rows=${rows}
        @bn-copy=${(e: CustomEvent<{ label: string }>) => toast(`${e.detail.label} copied`)}
      ></bn-kv>
      <div class="reveal-actions">
        <bn-button
          variant="primary"
          @click=${() => void this.copyToClipboard(key.secret, "Secret")}
          >Copy secret</bn-button
        >
      </div>
    `;
  }

  // ── Render: confirmations ───────────────────────────────────────────────

  /** The "never expires" gate. It runs **before** verification so the
   * five-minute grant stays fresh through a dialog the user may decline
   * (`SC-S202`), and it is rendered after the create dialog so it stacks above
   * it — DOM order decides stacking between two equal z-indexes. */
  private renderUnlimitedModal() {
    return html`<bn-modal
      ?open=${this.unlimitedConfirm !== null}
      tone="destructive"
      heading="Create a credential that never expires?"
      @close=${() => (this.unlimitedConfirm = null)}
    >
      <p class="copy">
        A credential with no expiry keeps working until someone revokes it. If it ever leaks — into
        a build log, a repository, a laptop backup — it stays valid indefinitely and nothing draws
        attention to it.
      </p>
      <p slot="consequence">
        Prefer a date unless this credential genuinely must outlive every rotation you will do.
      </p>
      <bn-button slot="footer" variant="ghost" @click=${() => (this.unlimitedConfirm = null)}
        >Back</bn-button
      >
      <bn-button slot="footer" variant="danger" @click=${() => this.confirmUnlimited()}
        >Create without an expiry</bn-button
      >
    </bn-modal>`;
  }

  /**
   * The step-up gate (`SC-F211`). A session token proves someone authenticated
   * in the last 24 hours; minting a credential that may live ten years demands
   * proof the account holder is here **now**. Rendered last so it stacks above
   * whichever dialog it interrupted.
   */
  private renderVerifyModal() {
    const sent = this.otpId !== "";
    // Offered while the answer is true or unknown: a failed passkey lookup must
    // not permanently strip the branch (`SC-S012`).
    const offerPasskey = this.hasPasskeys !== false;
    return html`<bn-modal
      ?open=${this.verifyFor !== null}
      heading="Verify it is you"
      @close=${() => this.closeVerify()}
    >
      <p class="copy">
        ${sent
          ? "We sent a one-time code to your email address. Enter it to continue."
          : "An API credential can outlive this browser session by years, so creating one needs a fresh check that you are here. The proof is single-use and valid for five minutes."}
      </p>
      ${offerPasskey
        ? html`<div class="verify-branch">
              <bn-button
                variant="secondary"
                block
                ?loading=${this.verifying}
                @click=${() => void this.verifyByPasskey()}
                >Verify with a passkey</bn-button
              >
            </div>
            <p class="verify-or">or</p>`
        : nothing}
      ${sent
        ? html`<bn-field label="One-time code" required>
            <bn-input
              mono
              inputmode="numeric"
              autocomplete="one-time-code"
              placeholder="123456"
              ?disabled=${this.verifying}
              .value=${this.otpCode}
              @input=${(e: CustomEvent<{ value: string }>) => (this.otpCode = e.detail.value)}
              @keydown=${(e: KeyboardEvent) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                void this.confirmCode();
              }}
            ></bn-input>
          </bn-field>`
        : nothing}
      <bn-button slot="footer" variant="ghost" @click=${() => this.closeVerify()}>Cancel</bn-button>
      ${sent
        ? html`<bn-button
            slot="footer"
            variant="primary"
            ?loading=${this.verifying}
            ?disabled=${this.otpCode.trim() === ""}
            @click=${() => void this.confirmCode()}
            >Verify</bn-button
          >`
        : html`<bn-button
            slot="footer"
            variant="primary"
            ?loading=${this.verifying}
            @click=${() => void this.sendCode()}
            >Email me a code</bn-button
          >`}
    </bn-modal>`;
  }

  private renderRevokeModal() {
    const target = this.revokeTarget;
    return html`<bn-modal
      ?open=${target !== null}
      tone="destructive"
      heading="Revoke this credential?"
      @close=${() => {
        if (!this.revoking) this.revokeTarget = null;
      }}
    >
      ${target
        ? html`<p class="copy">
            <span class="name">${target.name}</span> ·
            <span class="name">${target.display_prefix}</span> stops working immediately. Anything
            using it — CI, a script, the CLI — fails on its next request.
          </p>`
        : nothing}
      <p slot="consequence">
        Revocation takes effect on the next request, with no cache window: the credential's
        authority is rebuilt from its row every time it is used. It cannot be undone, and the row
        disappears from this page.
      </p>
      <bn-button
        slot="footer"
        variant="ghost"
        @click=${() => {
          if (!this.revoking) this.revokeTarget = null;
        }}
        >Cancel</bn-button
      >
      <bn-button
        slot="footer"
        variant="danger"
        ?loading=${this.revoking}
        @click=${() => void this.confirmRevoke()}
        >Revoke</bn-button
      >
    </bn-modal>`;
  }

  override render() {
    const populated = !this.loading && !this.loadError && this.keys.length > 0;
    return html`
      <div class="head">
        <div class="head-text">
          <h1>API keys</h1>
          <p class="subtitle">
            Long-lived credentials for calling Sluice Cloud outside the browser. They belong to your
            account, not to one organization, and they carry your authority — narrowed, never
            widened.
          </p>
        </div>
        ${populated
          ? html`<div class="head-actions">
              <bn-button variant="secondary" @click=${() => this.openCreate("fine_grained")}
                >New fine-grained token</bn-button
              >
              <bn-button variant="primary" @click=${() => this.openCreate("general")}
                >New general key</bn-button
              >
            </div>`
          : nothing}
      </div>

      <div class="stack">${this.renderListing()} ${this.renderNever()}</div>

      ${this.renderCreateModal()} ${this.renderRevokeModal()} ${this.renderUnlimitedModal()}
      ${this.renderVerifyModal()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-api-keys": PageApiKeys;
  }
}
